<script setup lang="ts">
  import { Chip, ChipSize, Icon, IconName, IconSize, Input, Spinner, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, nextTick, ref, watch } from 'vue';
  import type { SearchCriteriaHistory } from '@/entities/searchCriteria';
  import type { Country, Hotel } from '@/shared/model';
  import MobilePrimaryButton from '@/shared/ui/MobilePrimaryButton.vue';
  import SwipeView from '@/shared/ui/SwipeView.vue';
  import DestinationMobileCheckRow from './DestinationMobileCheckRow.vue';
  import DestinationMobileConfirm from './DestinationMobileConfirm.vue';
  import DestinationMobileEmpty from './DestinationMobileEmpty.vue';
  import DestinationMobileRow from './DestinationMobileRow.vue';
  import DestinationMobileSection from './DestinationMobileSection.vue';
  import { DESTINATION_MOBILE_TEXTS, DESTINATION_TEXTS } from '../config/texts';
  import { getVisaLabel } from '../lib/visaLabel';
  import { emptySelection, type Selection } from '../model/selection';
  import type { DestinationMobileStage } from '../model/types';
  import { useMobileDestination } from '../model/useMobileDestination';

  interface DestinationMobileProps {
    modelValue?: Selection | null;
    countries?: Country[];
    history?: SearchCriteriaHistory[];
    title?: string;
    hasSwitchedCountry?: boolean;
  }

  const props = withDefaults(defineProps<DestinationMobileProps>(), {
    modelValue: null,
    countries: () => [],
    history: () => [],
    title: DESTINATION_TEXTS.fieldLabelFixed,
    hasSwitchedCountry: false,
  });

  interface DestinationMobileEmits {
    (e: 'update:modelValue', value: Selection): void;
    (e: 'update:stage', value: DestinationMobileStage): void;
    (e: 'update:hasSwitchedCountry', value: boolean): void;
    (e: 'next'): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<DestinationMobileEmits>();

  const model = computed<Selection>({
    get: () => props.modelValue ?? emptySelection(),
    set: (value: Selection) => emit('update:modelValue', value),
  });

  const destination = useMobileDestination({
    model,
    countries: computed(() => props.countries ?? []),
    history: computed(() => props.history ?? []),
    hasSwitchedCountry: computed(() => props.hasSwitchedCountry),
    onSwitchedCountry: () => emit('update:hasSwitchedCountry', true),
  });

  const selectAllLabel = DESTINATION_TEXTS.selectAll;

  watch(
    [destination.view, destination.openedCountry],
    () => {
      emit('update:stage', {
        view: destination.view.value,
        countryName: destination.openedCountry.value?.name ?? null,
      });
    },
    { immediate: true },
  );

  const pageTitle = computed<string>(() =>
    destination.view.value === 'country' && destination.openedCountry.value
      ? destination.openedCountry.value.name
      : props.title,
  );

  const hasQuery = computed<boolean>(() => destination.query.value.trim() !== '');

  const focused = ref(false);
  const footerVisible = computed<boolean>(() => destination.count.value > 0 && !focused.value);

  const chipViews = computed(() =>
    destination.chips.value.map((chip) => {
      const allResortsBadge = destination.view.value === 'country' && chip.type === 'country';
      const countryName = destination.openedCountry.value?.name;
      return {
        chip,
        key: `${chip.type}-${chip.id}`,
        label: allResortsBadge ? (countryName ? `${selectAllLabel}, ${countryName}` : selectAllLabel) : chip.name,
        removable: chip.type !== 'country',
        truncate: chip.type !== 'country',
      };
    }),
  );

  const visaSub = (country: Country): { text: string; alert: boolean } => {
    const visa = getVisaLabel(country);
    return { text: visa.text, alert: visa.tone === 'brand' };
  };

  interface CountryRow {
    country: Country;
    key: string;
    name: string;
    sub: string;
    subAlert: boolean;
    badge: number;
    locked: boolean;
  }

  // Предвычисляем view-модель строки один раз в computed: getVisaLabel и проверка locked
  // считаются по разу на страну, а не по нескольку раз на каждый ре-рендер длинного списка.
  const toCountryRow = (country: Country, keyPrefix: string): CountryRow => {
    const visa = visaSub(country);
    const locked = destination.lockedCountry.value?.id === country.id;
    return {
      country,
      key: `${keyPrefix}-${country.id}`,
      name: country.name,
      sub: visa.text,
      subAlert: visa.alert,
      badge: locked ? destination.count.value : 0,
      locked,
    };
  };

  const recentRows = computed<CountryRow[]>(() =>
    destination.recentCountries.value.map((country) => toCountryRow(country, 'recent')),
  );

  const emptyRows = computed<CountryRow[]>(() =>
    (props.countries ?? []).map((country) => toCountryRow(country, 'empty-country')),
  );

  const catalogGroups = computed(() =>
    destination.countryGroups.value.map((group) => ({
      title: group.title,
      rows: group.items.flatMap((item) => (item.type === 'country' ? [toCountryRow(item.country, 'country')] : [])),
    })),
  );

  const searchCountryRows = computed(() =>
    destination.searchSections.value.countries.map((country) => {
      const visa = visaSub(country);
      return { country, key: `search-country-${country.id}`, name: country.name, sub: visa.text, subAlert: visa.alert };
    }),
  );

  const resortRowGroups = computed(() =>
    destination.resortGroups.value.map((group) => ({
      title: group.title,
      rows: group.items.flatMap((item) =>
        item.type === 'resort'
          ? [{ resort: item.resort, key: `resort-${item.resort.id}`, name: item.resort.name }]
          : [],
      ),
    })),
  );

  const showSpinner = computed<boolean>(() => {
    if (destination.view.value === 'country' && destination.mode.value === 'resorts') {
      return destination.loading.value && destination.resortGroups.value.length === 0 && !hasQuery.value;
    }
    return destination.searchLoading.value;
  });

  const hotelsHintVisible = computed<boolean>(
    () =>
      destination.view.value === 'country' &&
      destination.mode.value === 'hotels' &&
      !hasQuery.value &&
      destination.hotelRows.value.length === 0,
  );

  const contentRef = ref<HTMLElement | null>(null);

  const centerRowInList = (element: HTMLElement): void => {
    const list = element.closest('.destination-mobile__list');
    if (!(list instanceof HTMLElement)) {
      return;
    }
    const listRect = list.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    list.scrollTop += elementRect.top - listRect.top - (list.clientHeight - element.clientHeight) / 2;
  };

  watch(
    [() => destination.anchor.value, () => destination.resortGroups.value, () => destination.hotelRows.value],
    async () => {
      const target = destination.anchor.value;
      if (!target) {
        return;
      }
      await nextTick();
      const key = `${target.type}-${target.id}`;
      const element = contentRef.value?.querySelector(`[data-anchor="${key}"]`);
      if (!(element instanceof HTMLElement)) {
        return;
      }
      centerRowInList(element);
      destination.clearAnchor();
    },
  );

  const hotelSub = (hotel: Hotel): string => hotel.countryName;

  const back = (): boolean => destination.back();

  const onHeaderBack = (): void => {
    if (!destination.back()) {
      emit('close');
    }
  };

  defineExpose({ back });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    ref="contentRef"
    class="destination-mobile"
    data-testid="destination-mobile"
  >
    <SwipeView
      class="destination-mobile__swipe"
      :direction="destination.direction.value"
    >
      <div
        :key="destination.view.value"
        class="destination-mobile__page"
      >
        <div class="destination-mobile__header">
          <slot
            name="header"
            :title="pageTitle"
            :back="onHeaderBack"
          />
        </div>

        <div class="destination-mobile__search">
          <Input
            :model-value="destination.query.value"
            :placeholder="destination.placeholder.value"
            :bg="false"
            bordered
            size="l"
            name="destination-mobile-search"
            data-testid="destination-mobile-search"
            @update:modelValue="destination.setQuery"
            @focus="focused = true"
            @blur="focused = false"
          >
            <template #append>
              <button
                v-if="hasQuery"
                type="button"
                class="destination-mobile__icon-btn"
                data-testid="destination-mobile-search-clear"
                aria-label="Очистить"
                @click.stop.prevent="destination.setQuery('')"
              >
                <Icon
                  :name="IconName.Close"
                  :size="IconSize.S"
                />
              </button>
              <Icon
                v-else
                :name="IconName.Search"
                :size="IconSize.S"
                class="destination-mobile__search-icon"
              />
            </template>
          </Input>
        </div>

        <div
          v-if="chipViews.length > 0"
          class="destination-mobile__chips"
          data-testid="destination-mobile-chips"
        >
          <Chip
            v-for="item in chipViews"
            :key="item.key"
            class="destination-mobile__chip"
            appearance="filled"
            rounded
            :size="ChipSize.M"
          >
            <span
              class="destination-mobile__chip-text"
              :class="{ 'destination-mobile__chip-text_clip': item.truncate }"
              :title="item.truncate ? item.label : undefined"
            >
              {{ item.label }}
            </span>
            <template #append>
              <button
                v-if="item.removable"
                type="button"
                class="destination-mobile__icon-btn"
                data-testid="destination-mobile-chip-remove"
                aria-label="Убрать из выбора"
                @click.stop="destination.removeChip(item.chip)"
              >
                <Icon
                  :name="IconName.Close"
                  :size="IconSize.XS"
                />
              </button>
            </template>
          </Chip>
        </div>

        <div class="destination-mobile__list">
          <div
            v-if="showSpinner"
            class="destination-mobile__loading"
            data-testid="destination-mobile-loading"
          >
            <Spinner />
          </div>

          <template v-if="destination.view.value === 'root' && !hasQuery">
            <template v-if="recentRows.length > 0">
              <DestinationMobileSection :title="DESTINATION_MOBILE_TEXTS.recentTitle" />
              <DestinationMobileRow
                v-for="row in recentRows"
                :key="row.key"
                recent
                :name="row.name"
                :sub="row.sub"
                :sub-alert="row.subAlert"
                :badge="row.badge"
                :locked="row.locked"
                @click="destination.openCountry(row.country)"
              />
              <div class="destination-mobile__divider" />
            </template>

            <template v-for="group in catalogGroups">
              <DestinationMobileSection
                v-if="group.title"
                :key="`title-${group.title}`"
                :title="group.title ?? ''"
              />
              <DestinationMobileRow
                v-for="row in group.rows"
                :key="row.key"
                :name="row.name"
                :sub="row.sub"
                :sub-alert="row.subAlert"
                :badge="row.badge"
                :locked="row.locked"
                @click="destination.openCountry(row.country)"
              />
            </template>
          </template>

          <template v-else-if="destination.view.value === 'root'">
            <template v-if="destination.isSearchEmpty.value">
              <DestinationMobileEmpty />
              <DestinationMobileSection :title="DESTINATION_MOBILE_TEXTS.popularTitle" />
              <DestinationMobileRow
                v-for="row in emptyRows"
                :key="row.key"
                :name="row.name"
                :sub="row.sub"
                :sub-alert="row.subAlert"
                :badge="row.badge"
                :locked="row.locked"
                @click="destination.openCountry(row.country)"
              />
            </template>

            <template v-else>
              <template v-if="searchCountryRows.length > 0">
                <DestinationMobileSection
                  :title="DESTINATION_MOBILE_TEXTS.groupCountries"
                  :count="searchCountryRows.length"
                />
                <DestinationMobileRow
                  v-for="row in searchCountryRows"
                  :key="row.key"
                  :name="row.name"
                  :sub="row.sub"
                  :sub-alert="row.subAlert"
                  :highlight="destination.query.value"
                  @click="destination.openCountry(row.country)"
                />
              </template>

              <template v-if="destination.searchSections.value.resorts.length > 0">
                <DestinationMobileSection
                  :title="DESTINATION_MOBILE_TEXTS.groupResorts"
                  :count="destination.searchSections.value.resorts.length"
                />
                <DestinationMobileRow
                  v-for="resort in destination.searchSections.value.resorts"
                  :key="`search-resort-${resort.id}`"
                  :name="resort.name"
                  :sub="resort.countryName"
                  :highlight="destination.query.value"
                  @click="destination.pickSearchResult({ type: 'resort', resort })"
                />
              </template>

              <template v-if="destination.searchSections.value.hotels.length > 0">
                <DestinationMobileSection
                  :title="DESTINATION_MOBILE_TEXTS.groupHotels"
                  :count="destination.searchSections.value.hotels.length"
                />
                <DestinationMobileRow
                  v-for="hotel in destination.searchSections.value.hotels"
                  :key="`search-hotel-${hotel.id}`"
                  :name="hotel.name"
                  :sub="hotel.countryName"
                  :highlight="destination.query.value"
                  @click="destination.pickSearchResult({ type: 'hotel', hotel })"
                />
              </template>
            </template>
          </template>

          <template v-else-if="destination.mode.value === 'resorts'">
            <template v-if="!hasQuery">
              <DestinationMobileCheckRow
                :name="selectAllLabel"
                :checked="destination.allResortsChecked.value"
                data-testid="destination-mobile-select-all"
                @toggle="destination.toggleAllResorts"
              />
              <div class="destination-mobile__divider" />
            </template>

            <DestinationMobileEmpty v-if="destination.isSearchEmpty.value" />

            <template v-for="group in resortRowGroups">
              <DestinationMobileSection
                v-if="group.title"
                :key="`resort-title-${group.title}`"
                :title="group.title ?? ''"
              />
              <DestinationMobileCheckRow
                v-for="row in group.rows"
                :key="row.key"
                :data-anchor="`resort-${row.resort.id}`"
                :name="row.name"
                :checked="destination.checkedResortIds.value.has(row.resort.id)"
                :highlight="destination.query.value"
                @toggle="destination.toggleResort(row.resort)"
              />
            </template>
          </template>

          <template v-else>
            <DestinationMobileEmpty v-if="destination.isSearchEmpty.value" />

            <template v-if="destination.hotelRows.value.length > 0">
              <DestinationMobileSection :title="DESTINATION_MOBILE_TEXTS.groupHotels" />
              <DestinationMobileCheckRow
                v-for="hotel in destination.hotelRows.value"
                :key="`hotel-${hotel.id}`"
                :data-anchor="`hotel-${hotel.id}`"
                :name="hotel.name"
                :sub="hotelSub(hotel)"
                :checked="destination.checkedHotelIds.value.has(hotel.id)"
                :highlight="destination.query.value"
                @toggle="destination.toggleHotel(hotel)"
              />
            </template>

            <Typo
              v-if="hotelsHintVisible"
              class="destination-mobile__hint"
              data-testid="destination-mobile-hotels-hint"
              :variant="TypoVariant.BodySRegular"
              tag="p"
            >
              {{ DESTINATION_MOBILE_TEXTS.hotelsHint }}
            </Typo>
          </template>
        </div>

        <footer
          v-if="footerVisible"
          class="destination-mobile__footer"
          data-testid="destination-mobile-footer"
        >
          <MobilePrimaryButton
            testid="destination-mobile-next"
            @click="emit('next')"
          >
            {{ DESTINATION_MOBILE_TEXTS.next }} · {{ destination.count.value }}
          </MobilePrimaryButton>
        </footer>
      </div>
    </SwipeView>

    <DestinationMobileConfirm
      v-if="destination.confirmState.value"
      :state="destination.confirmState.value"
      @confirm="destination.confirmPending"
      @cancel="destination.cancelPending"
    />
  </div>
</template>

<style scoped lang="scss">
  .destination-mobile {
    box-sizing: border-box;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    position: relative;
    width: 100%;

    ::v-deep input[name='destination-mobile-search'] {
      font-size: 16px;
    }

    &__swipe {
      flex: 1 1 auto;
      min-height: 0;
    }

    &__page {
      background: var(--color-bg-primary);
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      inset: 0;
      position: absolute;
    }

    &__header {
      flex: 0 0 auto;
    }

    &__search {
      flex: 0 0 auto;
      padding: var(--spacing-s) var(--spacing-m) var(--spacing-xs);
    }

    &__search-icon {
      fill: var(--color-text-secondary);
    }

    &__icon-btn {
      align-items: center;
      background: none;
      border: none;
      cursor: pointer;
      display: inline-flex;
      fill: var(--color-text-secondary);
      justify-content: center;
      padding: 0;
    }

    &__chips {
      display: flex;
      flex: 0 0 auto;
      gap: var(--spacing-xxs);
      overflow-x: auto;
      padding: var(--spacing-xxs) var(--spacing-m) var(--spacing-xs);
      scrollbar-width: none;

      &::-webkit-scrollbar {
        height: 0;
      }
    }

    &__chip {
      flex: 0 0 auto;
    }

    &__chip-text_clip {
      display: block;
      max-width: 124px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__list {
      box-sizing: border-box;
      flex: 1 1 auto;
      min-height: 0;
      -webkit-overflow-scrolling: touch;
      overflow-y: auto;
      padding-bottom: 96px;
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: var(--spacing-l);
    }

    &__divider {
      background: var(--color-bg-secondary);
      height: 1px;
      margin: var(--spacing-xs) var(--spacing-m);
    }

    &__hint {
      color: var(--color-text-secondary);
      margin: 0;
      padding: var(--spacing-m);
      text-align: center;
    }

    &__footer {
      background: var(--color-bg-primary);
      border-radius: var(--rounding-xl) var(--rounding-xl) 0 0;
      bottom: 0;
      box-shadow: var(--shadow-m);
      box-sizing: border-box;
      left: 0;
      padding: var(--spacing-s) var(--spacing-m) var(--spacing-l);
      position: absolute;
      right: 0;
    }
  }
</style>
